import { useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useToast } from '@contexts/Toast.context';
import {
	COLLECTION_CONFIG_SCOPE,
	collectionConfigKey,
	invalidateCollectionConfig,
} from '@utils/collectionQueries';
import {
	buildDetectionObjectsPayload,
	saveDetectionObjectPatches,
} from '../utils/detectionObjectsPayload';

/**
 * The one write path for a collection's `detection_objects`.
 *
 * Three features now edit that array — detection pointers, inline object colors,
 * and the object filter panel — and the endpoint replaces it whole, so each save
 * is a read-modify-write of the same document. Sharing one hook means the parts
 * that are easy to get subtly different are defined once:
 *
 * - `COLLECTION_CONFIG_SCOPE` so React Query runs these strictly one at a time
 *   and no save is built off a pre-write state another save already moved on.
 * - An optimistic write into the `collectionConfig` cache, so `useCollectionData`
 *   derives the new value exactly as it derives a saved one — no second
 *   precedence rule to keep in sync, and no frame where the confirmed value
 *   flickers back while the refetch is in flight.
 * - Snapshot rollback on error, and invalidation in `onSettled` rather than
 *   `onSuccess` so a failed save still reconciles with the server.
 *
 * @param {string} collectionUuid
 * @param {Array}  objects - enriched collection objects from useCollectionData
 * @param {{ id: string, title: string }} errorToast - shown when the save fails
 */
const useCollectionConfigWrite = (collectionUuid, objects, errorToast) => {
	const queryClient = useQueryClient();
	const { addToast } = useToast();
	const configKey = collectionConfigKey(collectionUuid);

	const saveMutation = useMutation({
		scope: COLLECTION_CONFIG_SCOPE(collectionUuid),

		mutationFn: ({ patchesByKey }) =>
			saveDetectionObjectPatches(queryClient, collectionUuid, patchesByKey, objects),

		onMutate: async ({ patchesByKey }) => {
			await queryClient.cancelQueries({ queryKey: configKey });
			const previousConfig = queryClient.getQueryData(configKey);
			// `objects` is required here: without it an object missing from the
			// stored config is cached as a bare entry, and the next PATCH ships that
			// — wiping its color, filters and active flag server-side.
			queryClient.setQueryData(
				configKey,
				current =>
					current && {
						...current,
						detection_objects: buildDetectionObjectsPayload(
							patchesByKey,
							current.detection_objects,
							objects
						),
					}
			);
			return { previousConfig };
		},

		onError: (_error, _variables, context) => {
			// Put the map back where it was — the user's edit did not stick.
			if (context?.previousConfig !== undefined) {
				queryClient.setQueryData(configKey, context.previousConfig);
			}
			addToast({ ...errorToast, bg: 'danger', delay: 4000 });
		},

		// Never `setQueryData` from the mutation result: `updateCollection` returns
		// the normalized UI shape, not the raw config this key holds.
		onSettled: () => invalidateCollectionConfig(queryClient, collectionUuid),
	});

	const { mutate } = saveMutation;

	/**
	 * Persist a set of per-object patches.
	 *
	 * @param {Object} patchesByKey - { [objectKey]: { style?, filters?, is_active? } }
	 * @param {Object} [options] - per-call React Query callbacks (onSuccess, …)
	 */
	const save = useCallback(
		(patchesByKey, options) => mutate({ patchesByKey }, options),
		[mutate]
	);

	return { save, isSaving: saveMutation.isPending };
};

export default useCollectionConfigWrite;
